"use client";

import FusePageSimple from "@fuse/core/FusePageSimple";
import { motion } from "motion/react";
import AnalyticsDashboardAppHeader from "./AnalyticsDashboardAppHeader";
import Widgets from "@/components/widgets";
import { useGetDashboardStatsQuery } from "@/store/services/reports";
import { Skeleton, Typography } from "@mui/material";
import useUser from "@auth/useUser";
import { useMemo } from "react";
import ApplicationsTable from "@/pages/apps/applications/ApplicationsTable";
import DashboardInvoicesTable from "@/components/shared/DashboardInvoicesTable";
import { useGetInvoicesListQuery } from "@/store/services/subscriptions";

const container = {
  show: {
    transition: {
      staggerChildren: 0.04,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

function AnalyticsDashboardApp() {
  const { data: user } = useUser();

  const { data: dashboardStats, isFetching } = useGetDashboardStatsQuery(
    undefined,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    data: invoiceResponse,
    isLoading: isInvoiceLoading,
    isFetching: isInvoiceFetching,
  } = useGetInvoicesListQuery(
    {
      per_page: 5,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: user?.role !== "Admin",
    },
  );

  const invoices = useMemo(() => {
    return invoiceResponse?.data?.invoices ?? [];
  }, [invoiceResponse?.data?.invoices]);

  if (!user) {
    return null;
  }

  return (
    <FusePageSimple
      header={<AnalyticsDashboardAppHeader />}
      content={
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="mx-4"
        >
          {isFetching ? (
            <motion.div
              variants={item}
              className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full min-w-0 py-6 md:pb-8"
            >
              {Array.from({ length: user?.role === "Applicant" ? 4 : 6 }).map(
                (_, index) => (
                  <div key={index}>
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={141}
                      className="rounded-xl"
                    />
                  </div>
                ),
              )}
            </motion.div>
          ) : (
            <motion.div
              variants={item}
              className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full min-w-0 py-6 md:pb-8"
            >
              {user?.role === "Applicant" && (
                <>
                  <Widgets
                    title="Address"
                    name="Total Addresses"
                    value={String(
                      dashboardStats?.data?.total_addresses_with_documents_count
                        ?.total_addresses || "0",
                    )}
                    nameColor="text-blue-600"
                    valueColor="text-blue-500"
                    redirectLink="/apps/addresses"
                  />
                  <Widgets
                    title="Documents"
                    name="Total Documents"
                    value={String(
                      dashboardStats?.data?.total_addresses_with_documents_count
                        ?.total_documents || "0",
                    )}
                    nameColor="text-green-600"
                    valueColor="text-green-500"
                    redirectLink="/apps/documents"
                  />
                  <Widgets
                    title="Assets"
                    name="Total Number of Assets"
                    value={String(
                      dashboardStats?.data?.total_applications_assets_count ||
                        "0",
                    )}
                    nameColor="text-purple-600"
                    valueColor="text-purple-500"
                    redirectLink="/apps/applications"
                  />
                  <Widgets
                    title="Assets Value"
                    name="Assets Total Value"
                    value={String(
                      dashboardStats?.data?.total_applications_assets_value ||
                        "0",
                    )}
                    nameColor="text-amber-600"
                    valueColor="text-amber-500"
                    showDollar={true}
                  />
                </>
              )}

              {user?.role === "Admin" && (
                <>
                  {" "}
                  <Widgets
                    title="Subscribed Users"
                    name="Total Users Who Have Subscribed"
                    value={String(
                      dashboardStats?.data?.subscribed_users_count || "0",
                    )}
                    nameColor="text-amber-600"
                    valueColor="text-amber-500"
                    showDollar={false}
                  />
                  <Widgets
                    title="Unsubscribed Users"
                    name="Users Who Have Unsubscribed"
                    value={String(
                      dashboardStats?.data?.unsubscribed_users_count || "0",
                    )}
                    nameColor="text-red-600"
                    valueColor="text-red-500"
                    showDollar={false}
                  />
                  <Widgets
                    title="New Users"
                    name="Users Who Just Registered"
                    value={String(
                      dashboardStats?.data?.just_registered_users_count || "0",
                    )}
                    nameColor="text-blue-600"
                    valueColor="text-blue-500"
                  />
                  <Widgets
                    title="Total Users"
                    name="Total Number of Registered Users"
                    value={String(
                      dashboardStats?.data?.total_users_count || "0",
                    )}
                    nameColor="text-purple-600"
                    valueColor="text-purple-500"
                  />
                  <Widgets
                    title="Active Users"
                    name="Users Currently Active"
                    value={String(
                      dashboardStats?.data?.active_users_count || "0",
                    )}
                    nameColor="text-teal-600"
                    valueColor="text-teal-500"
                  />
                  <Widgets
                    title="Inactive Users"
                    name="Users Currently Inactive"
                    value={String(
                      dashboardStats?.data?.inactive_users_count || "0",
                    )}
                    nameColor="text-gray-600"
                    valueColor="text-gray-500"
                  />
                </>
              )}
            </motion.div>
          )}

          {user?.role === "Applicant" ? (
            <motion.div variants={item} className="mt-5 mb-3">
              <div className="w-full mb-6">
                <Typography className="text-4xl font-bold tracking-tight leading-none">
                  Assets
                </Typography>
              </div>
              <ApplicationsTable limit={5} isDashboard={true} isView={true} />
            </motion.div>
          ) : (
            <motion.div
              variants={item}
              className="mt-5 mb-3 flex flex-col md:flex-row gap-4 items-start"
            >
              <div className="w-full mt-3 md:mt-0">
                <div className="w-full mb-4">
                  <Typography className="text-4xl font-bold leading-none tracking-tight">
                    Transactions
                  </Typography>
                </div>

                <DashboardInvoicesTable
                  invoices={invoices}
                  isLoading={isInvoiceLoading}
                  isFetching={isInvoiceFetching}
                  limit={5}
                />
              </div>
            </motion.div>
          )}
        </motion.div>
      }
    />
  );
}

export default AnalyticsDashboardApp;
